// eslint-disable-next-line
///<reference path="../../../../../jsdoc-types.js" />

import mapKeys from 'lodash/mapKeys';
import zipWith from 'lodash/zipWith';
import sitePreferencesConfig from '../../../../../config/sitePreferencesConfig.json';
import { pickByKeyPrefix } from '../../common/utils/objectUtils';
import { sortArrayOfNumbers } from '../../common/utils/arrayUtils';
import { getSrcsetFromUrl } from '../../common/utils/imageTransformation';
import getFrontConfig from '../../common/getFrontConfig';

const FRONT_CONFIG = getFrontConfig();

/**
 * Transform the ProductImages model of SFRA (a type-based map of arrays of
 * images) to an unique array of objects that group all the types of a single
 * image.
 *
 * From:
 * {
 *   ...
 *   "medium": Image[],
 *   "plp.200": Image[],
 *   ...
 * }
 *
 * We get:
 * [
 *   {
 *     ...
 *     "medium": Image,
 *     "plp.200": Image,
 *     ...
 *   },
 *   {
 *     ...
 *     "medium": Image,
 *     "plp.200": Image,
 *     ...
 *   },
 *   ...
 * ]
 *
 * @param {ProductImages} productImages SFRA type-based map of image arrays
 * @returns {ProductImageSet[]} array of image sets grouped by single image
 */
function createProductImageSetArrayFromProductImages(productImages) {
  let result = [];
  if (productImages) {
    const types = Object.keys(productImages);
    const imageArrays = Object.values(productImages);
    result = zipWith(...imageArrays, (...images) =>
      // build an object of types an corresponding image
      images.reduce((res, image, index) => {
        const typeId = types[index];
        res[typeId] = image;
        return res;
      }, {})
    );
  }
  return result;
}

export const productImageSetMixin = {
  computed: {
    /**
     * An unique array of objects which group all the different types of a
     * single image.
     * @returns {ProductImageSet[]} grouped image sets for the current product
     */
    productImageFullSetArray() {
      return createProductImageSetArrayFromProductImages(this.productImages);
    },
  },

  methods: {
    /**
     * Get the default image (usually the smallest one) from a image set.
     * @param {ProductImageSet} productImageSet image set to pick from
     * @returns {Image|Object} the default image, or an empty object
     */
    getDefaultImageFromProductImageSet(productImageSet) {
      if (!productImageSet) return {};
      const types = Object.keys(productImageSet);
      const lowestWidth = sortArrayOfNumbers(types).shift();
      const defaultImageKey = Number.isNaN(lowestWidth)
        ? types[0]
        : `${lowestWidth}`;
      return productImageSet[defaultImageKey];
    },

    /**
     * Get the placeholder image from a image set. Returns null if no placeholder
     * is defined.
     * @param {ProductImageSet} productImageSet image set to pick the placeholder from
     * @returns {Image} the placeholder image, or null if none is defined
     */
    getPlaceholderImageFromProductImageSet(productImageSet) {
      if (
        FRONT_CONFIG.productImages.placeholderType !==
        sitePreferencesConfig.customAttributes.imagePlaceholderType.values
          .blurryImage
      ) {
        return null;
      }
      return productImageSet.placeholder ?? null;
    },

    /**
     * Get the default image URL from an image set. Safe accessor for use in
     * templates, where optional chaining (`?.`) is not supported by the Vue 2
     * template compiler (buble).
     * @param {ProductImageSet} productImageSet image set to pick from
     * @returns {string|undefined} the default image URL, or undefined
     */
    getDefaultImageUrlFromProductImageSet(productImageSet) {
      return (this.getDefaultImageFromProductImageSet(productImageSet) || {})
        .url;
    },

    /**
     * Get the default image alt text from an image set. Safe accessor for use
     * in templates (no optional chaining; see buble limitation).
     * @param {ProductImageSet} productImageSet image set to pick from
     * @returns {string|undefined} the default image alt text, or undefined
     */
    getDefaultImageAltFromProductImageSet(productImageSet) {
      return (this.getDefaultImageFromProductImageSet(productImageSet) || {})
        .alt;
    },

    /**
     * Get the placeholder image URL from an image set. Safe accessor for use in
     * templates (no optional chaining; see buble limitation).
     * @param {ProductImageSet} productImageSet image set to pick the placeholder from
     * @returns {string|undefined} the placeholder image URL, or undefined
     */
    getPlaceholderImageUrlFromProductImageSet(productImageSet) {
      return (
        this.getPlaceholderImageFromProductImageSet(productImageSet) || {}
      ).url;
    },

    /**
     * Creates an array of image sets related by the image type prefix.
     *
     * From:
     * {
     *   ...
     *   "medium": Image[],
     *   "plp.200": Image[],
     *   "plp.400": Image[],
     *   "plp.600": Image[],
     *   "plp.800": Image[],
     *   "plp.1200": Image[],
     *   ...
     * }
     *
     * We get this for a set named "plp":
     * [
     *   {
     *     "200": Image,
     *     "400": Image,
     *     "600": Image,
     *     "800": Image,
     *     "1200": Image,
     *   },...
     * ]
     *
     * @param {string} [imageSetName='plp'] image set prefix to filter by (e.g. 'plp', 'pdp')
     * @param {ProductImages} [customImages=null] image map to use instead of this.productImages
     * @returns {ProductImageSet[]} image sets keyed by width for the given prefix
     */
    getProductImageSets(
      imageSetName = FRONT_CONFIG.productImages.imageSets.PLP,
      customImages = null
    ) {
      const imageSetTypePrefix = `${imageSetName}.`;
      const filteredProductImages = pickByKeyPrefix(
        customImages || this.productImages,
        imageSetTypePrefix
      );
      const imageArraysByImageSetTypes = mapKeys(
        filteredProductImages,
        (_, key) => key.substr(imageSetTypePrefix.length)
      );
      return createProductImageSetArrayFromProductImages(
        imageArraysByImageSetTypes
      );
    },

    /**
     * Creates the value of srcset property for the given image set.
     * ProductImageSet keys have to be widths.
     * @param {ProductImageSet} productImageSet image set whose keys are widths
     * @returns {string} srcset value
     */
    getSrcsetFromProductImageSet(productImageSet) {
      if (!productImageSet) return '';
      return Object.entries(productImageSet)
        .map(([type, image]) => {
          const width = +type; // Convert to number
          if (Number.isNaN(width)) return null;
          if (!image?.url) return null;
          return `${image.url} ${width}w`;
        })
        .filter((identity) => identity) // Remove null entries
        .join(', ');
    },

    /**
     * Creates the value of srcset property from a single base image URL and a
     * list of widths, applying each width as the DIS scaleWidth (sw) param.
     * Useful when the server returns one base URL instead of a full image set.
     * @param {string} url Base image URL
     * @param {number[]} widths Target widths in px
     * @param {ImageTransformations} [extraParams={}] Extra DIS params (e.g. { q: 80 })
     * @returns {string} srcset value
     */
    getSrcsetFromUrl(url, widths, extraParams) {
      return getSrcsetFromUrl(url, widths, extraParams);
    },
  },
};

export default productImageSetMixin;
